import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import Swal from 'sweetalert2';
import { SousCategorieService } from 'app/services/sous-categorie/sous-categorie.service';
import { ProduitsService } from 'app/services/produits/produits.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';

interface SousProduit {
  id: number;
  name: string;
  image_url: string;
  category: string;
  prix: number;
  displayOrder?: number;
  orderIndex?: number;
  createdAt?: string;
  listimages?: string[] | string;
  [key: string]: any;
}

@Component({
  selector: 'app-sous-produits-list',
  templateUrl: './sous-produits-list.component.html',
  styleUrls: ['./sous-produits-list.component.scss']
})
export class SousProduitsListComponent implements OnInit {
  idProduit: any;
  produit: any;
  list: SousProduit[] = [];
  isLoading = false;
  agencyId: string | null = null;
  private readonly s3BaseUrl = 'https://tunisie-immob.s3.eu-west-3.amazonaws.com/';

  constructor(
    private sousCategorieService: SousCategorieService,
    private produitsService: ProduitsService,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private router: Router,
    private awsService: AwscontrolService
  ) { }

  ngOnInit(): void {
    this.idProduit = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadSousProduits();
  }

  // ---------------------------------------------
  // FIXED: use the product's embedded 'sous_cat' array
  // ---------------------------------------------
  loadSousProduits(): void {
    this.isLoading = true;
    this.produitsService.getProduit(this.idProduit).subscribe({
      next: (product: any) => {
        this.produit = product;

        // Extract agency ID (for navigation)
        if (product.user?.id) {
          this.agencyId = product.user.id.toString();
        } else if (product.userId) {
          this.agencyId = product.userId.toString();
        } else if (product.agencyId) {
          this.agencyId = product.agencyId.toString();
        }

        // Use the sub‑categories already returned with the product
        if (product.sous_cat && Array.isArray(product.sous_cat)) {
          this.list = product.sous_cat;
          this.initializeOrderIndexes();
        } else {
          // Fallback: if for some reason the API does not include sous_cat,
          // you can keep the old method (but it will be slower)
          this.loadSousCategoriesFallback();
          return;
        }

        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.isLoading = false;
        Swal.fire('Erreur', 'Produit introuvable.', 'error');
      }
    });
  }

  // Optional fallback (remove if you are sure the API always includes sous_cat)
  private loadSousCategoriesFallback(): void {
    this.sousCategorieService.getListSousCategorie().subscribe({
      next: (allSousCats: any[]) => {
        const sousList = allSousCats.filter(
          sc => sc.produit_id === parseInt(this.idProduit) || sc.produitId === parseInt(this.idProduit)
        );
        this.list = sousList;
        this.initializeOrderIndexes();
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.isLoading = false;
        Swal.fire('Erreur', 'Impossible de charger les sous-produits.', 'error');
      }
    });
  }

  // ---------- Order management (unchanged) ----------
  private initializeOrderIndexes(): void {
    const hasOrder = this.list.some(p => p.displayOrder !== undefined && p.displayOrder !== null);
    if (hasOrder) {
      this.list.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    } else {
      this.list.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
    }
    this.list.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
  }

  private updateOrdersAndSave(): void {
    this.list.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
    this.saveAllOrders();
  }

  private saveAllOrders(): void {
    if (this.list.length === 0) return;
    this.isLoading = true;
    let completed = 0;
    let hasError = false;

    this.list.forEach(sousProduit => {
      this.sousCategorieService.updateSousCategorieOrder(sousProduit.id, sousProduit.displayOrder!).subscribe({
        next: () => {
          completed++;
          if (completed === this.list.length && !hasError) {
            this.isLoading = false;
            Swal.fire({
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 1500,
              icon: 'success',
              title: 'Ordre mis à jour'
            });
          }
        },
        error: (err) => {
          console.error(`Error updating order for sous-produit ${sousProduit.id}:`, err);
          hasError = true;
          this.isLoading = false;
          Swal.fire({
            title: 'Erreur',
            text: 'Impossible de sauvegarder l\'ordre. Rechargement...',
            icon: 'error'
          });
          this.loadSousProduits();
        }
      });
    });
  }

  // ---------- Drag & drop (unchanged) ----------
  drop(event: CdkDragDrop<SousProduit[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.list, event.previousIndex, event.currentIndex);
    this.updateOrdersAndSave();
  }

  moveUp(index: number): void {
    if (index === 0) return;
    [this.list[index], this.list[index - 1]] = [this.list[index - 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  moveDown(index: number): void {
    if (index === this.list.length - 1) return;
    [this.list[index], this.list[index + 1]] = [this.list[index + 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  // ---------- Helper (unchanged) ----------
  getMainImageUrl(imageUrlString: string | null | undefined): string | null {
    if (!imageUrlString || imageUrlString.trim() === '') return null;
    const urls = imageUrlString.split(',').map(url => url.trim()).filter(url => url);
    if (urls.length === 0) return null;
    const first = urls[0];
    if (first.startsWith('http')) return first;
    return this.s3BaseUrl + first;
  }

  // ---------- Delete (unchanged) ----------
  deleteSousProduit(sousProduit: SousProduit): void {
    Swal.fire({
      title: "Êtes-vous sûr(e) ?",
      text: `Supprimer "${sousProduit.name}" ? Toutes ses images seront également supprimées.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler"
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Suppression en cours...',
          text: 'Veuillez patienter',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => Swal.showLoading()
        });

        const imageUrls: string[] = [];

        if (sousProduit.image_url) {
          const mainImages = sousProduit.image_url.split(',').map(u => u.trim()).filter(u => u);
          imageUrls.push(...mainImages);
        }

        if (sousProduit.listimages) {
          let secondaryUrls: string[] = [];
          if (Array.isArray(sousProduit.listimages)) {
            secondaryUrls = sousProduit.listimages;
          } else if (typeof sousProduit.listimages === 'string') {
            try {
              const parsed = JSON.parse(sousProduit.listimages);
              if (Array.isArray(parsed)) {
                secondaryUrls = parsed;
              }
            } catch {
              secondaryUrls = sousProduit.listimages.split(',').map(u => u.trim()).filter(u => u);
            }
          }
          imageUrls.push(...secondaryUrls);
        }

        const uniqueUrls = [...new Set(imageUrls.filter(url => url && url.trim() !== ''))];

        if (uniqueUrls.length === 0) {
          this.deleteRecord(sousProduit.id);
          return;
        }

        const deletionPromises = uniqueUrls.map(url =>
          this.awsService.deleteFile(url).toPromise()
        );

        Promise.allSettled(deletionPromises)
          .then((results) => {
            const failed = results.filter(r => r.status === 'rejected');
            if (failed.length > 0) {
              console.warn(`Failed to delete ${failed.length} image(s) from AWS. Continuing with record deletion.`);
            }
            this.deleteRecord(sousProduit.id);
          })
          .catch((err) => {
            console.error('Error during image deletion:', err);
            this.deleteRecord(sousProduit.id);
          });
      }
    });
  }

  private deleteRecord(id: number): void {
    this.sousCategorieService.deleteSousCategorie(id).subscribe({
      next: () => {
        Swal.close();
        Swal.fire('Supprimé!', 'Le sous-produit et ses images ont été supprimés.', 'success');
        this.list = this.list.filter(p => p.id !== id);
        this.updateOrdersAndSave();
      },
      error: (err) => {
        Swal.close();
        console.error('Delete record error:', err);
        Swal.fire('Erreur', 'Impossible de supprimer le sous-produit.', 'error');
      }
    });
  }

  // ---------- Navigation back (unchanged) ----------
  navigateBack(): void {
    if (this.agencyId) {
      this.router.navigate(['/list-produits', this.agencyId]);
    } else {
      this.location.back();
    }
  }
}